import { useState, useEffect, useRef } from "@wordpress/element";

/**
 * AudioWaveform Component
 *
 * Renders an animated audio waveform visualization with customizable appearance.
 *
 * @param {Object} props Component properties
 * @param {boolean} props.isSpeaking Whether the waveform should animate (speaking state)
 * @param {number} props.size Size of the waveform circle in pixels
 * @param {string} props.waveformColor Color of the waveform bars
 * @param {string} props.circleColor Background color of the circle (supports any valid CSS color value)
 * @param {number} props.waveformWidth Width of each waveform bar in pixels
 * @returns {JSX.Element} The AudioWaveform component
 */
const AudioWaveform = ({
  isSpeaking,
  size = 60,
  waveformColor = "#FFFFFF",
  circleColor = "#000000",
  waveformWidth = 1,
}) => {
  // Default static bars for non-speaking state
  const staticBars = [15, 30, 15, 20, 15, 30, 15];
  const [bars, setBars] = useState(staticBars);
  const timeoutRef = useRef(null);

  /**
   * Generate random bar heights for speaking animation
   * @returns {Array} Array of bar heights
   */
  const generateSpeakingBars = () => {
    return [
      Math.floor(Math.random() * 18) + 4,
      Math.floor(Math.random() * 20) + 10,
      Math.floor(Math.random() * 24) + 4,
      Math.floor(Math.random() * 20) + 8,
      Math.floor(Math.random() * 18) + 4,
      Math.floor(Math.random() * 18) + 10,
      Math.floor(Math.random() * 18) + 4,
    ];
  };

  // Effect to handle animation state changes
  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (!isSpeaking) {
      setBars(staticBars);
      return;
    }

    const animateBars = () => {
      setBars(generateSpeakingBars());
      timeoutRef.current = setTimeout(animateBars, 150);
    };

    animateBars();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isSpeaking]);

  // Scale the bars based on container size
  const scaleFactor = (size / 60) * 0.85;

  return (
    <div
      className="va-relative va-flex va-items-center va-justify-center va-rounded-full va-overflow-hidden"
      style={{
        width: `${size}px`,
        height: `${size}px`,
        backgroundColor: circleColor, // Accepts any valid CSS color value (hex, rgb, rgba, transparent, etc.)
      }}
    >
      <div className="va-flex va-items-center va-justify-center va-h-full va-w-full">
        {bars.map((height, index) => (
          <div
            key={index}
            className="va-mx-[1px] va-rounded-sm"
            style={{
              height: `${height * scaleFactor}px`,
              width: `${waveformWidth}px`,
              backgroundColor: waveformColor,
              transition: "height 0.1s ease-in-out",
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default AudioWaveform;
